/**
 * Utility functions for formatting data in the WirelessAdvisor components
 */

import { WIRELESS_SPECS } from './constants';

/**
 * Format speed from Mbps to a human-readable string (Mbps or Gbps)
 * @param {number} speedMbps - Speed in megabits per second
 * @returns {string} - Formatted speed string
 */
export const formatSpeed = (speedMbps) => {
  return speedMbps >= 1000 
    ? `${(speedMbps / 1000).toFixed(1)} Gbps` 
    : `${speedMbps} Mbps`;
};

/**
 * Format area in square meters
 * @param {number} area - Area in square meters
 * @returns {string} - Formatted area string
 */
export const formatArea = (area) => {
  return `${area} m²`;
};

/**
 * Format percentage with % symbol
 * @param {number} percentage - Percentage value
 * @returns {string} - Formatted percentage string
 */
export const formatPercentage = (percentage) => {
  return `${Math.round(percentage)}%`;
};

/**
 * Format room name for display
 * @param {string} room - Room identifier
 * @returns {string} - Formatted room name
 */
export const formatRoomName = (room) => {
  if (room === 'otherRoom') return 'Other Room';
  return room.charAt(0).toUpperCase() + room.slice(1);
};

/**
 * Get CSS classes for a router card based on its standard
 * @param {string} routerStandard - Router standard (WIFI_5, WIFI_6, etc.)
 * @returns {Object} - Object with CSS class strings
 */
export const getRouterCardClasses = (routerStandard) => {
  const isWifi6OrBetter = routerStandard === 'WIFI_6' || routerStandard === 'WIFI_6E' || routerStandard === 'WIFI_7';
  const isWifi7 = routerStandard === 'WIFI_7';
  
  return {
    card: `bg-gray-800 rounded-xl overflow-hidden border ${
      isWifi7
        ? 'border-blue-500/30 shadow-lg shadow-blue-500/10' // WiFi 7
        : isWifi6OrBetter
          ? 'border-purple-500/30 shadow-lg shadow-purple-500/10' // WiFi 6/6E
          : 'border-purple-500/20 border-opacity-50' // WiFi 5
    }`,
    badge: `w-full py-1 text-center text-xs font-medium ${
      isWifi7
        ? 'bg-blue-600/20 text-blue-400'
        : 'bg-purple-600/20 text-purple-400'
    }`,
    icon: `h-5 w-5 rounded-full mr-2 flex items-center justify-center ${
      isWifi7
        ? 'bg-blue-600/20'
        : 'bg-purple-600/20'
    }`,
    progressBar: `h-full ${
      isWifi7
        ? 'bg-gradient-to-r from-blue-500 to-cyan-500'
        : 'bg-gradient-to-r from-purple-500 to-pink-500'
    }`,
    button: `w-full py-2 text-white rounded-lg text-sm font-medium transition-colors ${
      isWifi7
        ? 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700'
        : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
    }`
  };
};

/**
 * Get CSS classes for coverage indicators
 * @param {string} coverageType - Coverage type (high, medium, low)
 * @returns {Object} - Object with CSS class strings
 */
export const getCoverageClasses = (coverageType) => {
  switch (coverageType) {
    case 'high':
      return {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        heading: 'text-blue-600',
        border: 'border-blue-200',
        indicator: 'bg-blue-600'
      };
    case 'medium':
      return {
        bg: 'bg-orange-50',
        text: 'text-orange-700',
        heading: 'text-orange-600',
        border: 'border-orange-200',
        indicator: 'bg-orange-500'
      };
    case 'low':
      return {
        bg: 'bg-red-50',
        text: 'text-red-700',
        heading: 'text-red-600',
        border: 'border-red-200',
        indicator: 'bg-red-500'
      };
    default:
      return {
        bg: 'bg-gray-50',
        text: 'text-gray-700',
        heading: 'text-gray-600',
        border: 'border-gray-200',
        indicator: 'bg-gray-500'
      };
  }
};

/**
 * Get a human-readable description for a router standard
 * @param {string} routerStandard - Router standard (WIFI_5, WIFI_6, etc.)
 * @returns {Object} - Object with description and features
 */
export const getRouterStandardDescription = (routerStandard) => {
  switch (routerStandard) {
    case 'WIFI_5':
      return {
        title: 'WiFi 5 (802.11ac)',
        description: 'Good for basic internet usage and smaller homes',
        features: [
          'Supports speeds up to 3.5 Gbps (theoretical)',
          'Operates on 5 GHz and 2.4 GHz bands',
          'Good for homes with fewer devices',
          'Suitable for general browsing, email, and HD streaming'
        ]
      };
    case 'WIFI_6':
      return {
        title: 'WiFi 6 (802.11ax)',
        description: 'Better performance in homes with many devices',
        features: [
          'Supports speeds up to 9.6 Gbps (theoretical)',
          'Improved efficiency with multiple devices',
          'Better battery life for connected devices',
          'Enhanced performance in dense environments',
          'Ideal for smart homes and multiple HD/4K streams'
        ]
      };
    case 'WIFI_6E':
      return {
        title: 'WiFi 6E',
        description: 'Extended WiFi 6 with 6 GHz band for less interference',
        features: [
          'All benefits of WiFi 6',
          'Additional 6 GHz band for less congestion',
          'More available channels for better performance',
          'Lower latency for gaming and video calls',
          'Best for homes with many high-bandwidth devices'
        ]
      };
    case 'WIFI_7':
      return {
        title: 'WiFi 7 (802.11be)',
        description: 'Cutting-edge performance for demanding applications',
        features: [
          'Supports speeds up to 40 Gbps (theoretical)',
          'Ultra-low latency for real-time applications',
          'Multi-Link Operation for improved reliability',
          'Enhanced efficiency across all bands (2.4/5/6 GHz)',
          'Future-proof for next-generation applications',
          'Perfect for 8K streaming, VR/AR, and cloud gaming'
        ]
      };
    default:
      return {
        title: 'Unknown Standard',
        description: 'Information not available',
        features: []
      };
  }
};

/**
 * Get a human-readable explanation for the coverage percentages
 * @param {Object} coveragePercentages - Coverage percentages object
 * @returns {string} - Explanation for the coverage
 */
export const getCoverageExplanation = (coveragePercentages) => {
  const { high, medium, low } = coveragePercentages;
  
  if (high >= 90) {
    return 'Excellent coverage throughout your home. You should experience fast, reliable WiFi in all areas.';
  } else if (high >= 70) {
    return 'Very good coverage in most areas of your home. You may experience slight speed reductions in some corners.';
  } else if (high >= 50) {
    return 'Good coverage in main areas. Some rooms may have reduced speeds, but still suitable for most activities.';
  } else if (medium >= 80) {
    return 'Moderate coverage. Main areas will have good speeds, but some rooms may only be suitable for basic browsing.';
  } else if (medium >= 50) {
    return 'Limited high-speed coverage. Consider adding access points for better performance throughout your home.';
  } else {
    return 'Poor coverage in many areas. A mesh WiFi system is strongly recommended for your home layout.';
  }
};

/**
 * Get device recommendations based on router standard and home size
 * @param {string} routerStandard - Router standard (WIFI_5, WIFI_6, etc.)
 * @param {string} homeSize - Home size category (SMALL, MEDIUM, etc.)
 * @returns {Array} - Array of recommended devices
 */
export const getDeviceRecommendations = (routerStandard, homeSize) => {
  const recommendations = [];
  const homeSizeObj = WIRELESS_SPECS.HOME_SIZES[homeSize];
  const area = homeSizeObj ? homeSizeObj.max : 300;
  
  // Base recommendation based on router standard
  switch (routerStandard) {
    case 'WIFI_5':
      recommendations.push({
        type: 'router',
        name: 'Mid-range WiFi 5 Router',
        description: 'A reliable WiFi 5 router suitable for basic internet usage',
        features: ['Dual-band', 'Multiple antennas', 'Gigabit Ethernet ports']
      });
      break;
    case 'WIFI_6':
      recommendations.push({
        type: 'router',
        name: 'WiFi 6 Router',
        description: 'A modern WiFi 6 router for improved performance with multiple devices',
        features: ['Dual-band', 'OFDMA technology', 'MU-MIMO', 'Beamforming']
      });
      break;
    case 'WIFI_6E':
      recommendations.push({
        type: 'router',
        name: 'WiFi 6E Router',
        description: 'Advanced WiFi 6E router with 6 GHz band support',
        features: ['Tri-band (2.4/5/6 GHz)', 'Ultra-wide channels', 'Low latency']
      });
      break;
    case 'WIFI_7':
      recommendations.push({
        type: 'router',
        name: 'WiFi 7 Router',
        description: 'Cutting-edge WiFi 7 router for maximum performance',
        features: ['Multi-Link Operation', '320 MHz channels', 'Extremely low latency']
      });
      break;
  }
  
  // Add mesh recommendation for larger homes
  if (area > 200 || homeSize === 'LARGE' || homeSize === 'XLARGE') {
    switch (routerStandard) {
      case 'WIFI_5':
        recommendations.push({
          type: 'mesh',
          name: 'WiFi 5 Mesh System (2-3 units)',
          description: 'A mesh system to extend coverage throughout your larger home',
          features: ['Seamless roaming', 'Easy setup', 'Whole-home coverage']
        });
        break;
      case 'WIFI_6':
      case 'WIFI_6E':
        recommendations.push({
          type: 'mesh',
          name: `${WIRELESS_SPECS.ROUTER_SPECS.ROUTER_STANDARDS[routerStandard].LABEL} Mesh System (2-3 units)`,
          description: `A ${WIRELESS_SPECS.ROUTER_SPECS.ROUTER_STANDARDS[routerStandard].LABEL} mesh system for optimal coverage in your larger home`,
          features: ['Seamless roaming', 'Advanced backhaul', 'Smart network optimization']
        });
        break;
      case 'WIFI_7':
        recommendations.push({
          type: 'mesh',
          name: 'WiFi 7 Mesh System (2-3 units)',
          description: 'A premium WiFi 7 mesh system for ultimate performance throughout your home',
          features: ['Multi-Link Operation', 'AI-driven optimization', 'Future-proof technology']
        });
        break;
    }
  }
  
  // Add range extender recommendation for medium homes with specific router
  if ((area > 150 && area <= 250) && routerStandard === 'WIFI_5') {
    recommendations.push({
      type: 'extender',
      name: 'WiFi Range Extender',
      description: 'A range extender to boost coverage in dead zones',
      features: ['Easy setup', 'Extends existing network', 'Budget-friendly option']
    });
  }
  
  return recommendations;
};

/**
 * Format wall density description
 * @param {number} wallsBetweenPoints - Wall density value
 * @returns {string} - Formatted wall density description
 */
export const formatWallDensity = (wallsBetweenPoints) => {
  switch (wallsBetweenPoints) {
    case 0:
      return "Open plan layout with few walls";
    case 1:
      return "Standard layout with some walls between rooms";
    case 2:
      return "Dense layout with many walls";
    case 3:
    default:
      return "Very dense layout with thick/many walls";
  }
};